import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Company, CreateCompanyRequest } from "../types/company";
import {
  ApplicationWithCompany,
  CreateApplicationRequest,
  UpdateApplicationRequest,
} from "../types/application";
import { ApiResponse, CompanyApiResponse } from "../types/auth";

interface Memo {
  id: number;
  company_id: number;
  content: string;
  created_at: string;
  updated_at: string;
}

const CompanyDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company | null>(null);
  const [application, setApplication] = useState<ApplicationWithCompany | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditingCompany, setIsEditingCompany] = useState<boolean>(false);
  const [isEditingApplication, setIsEditingApplication] =
    useState<boolean>(false);
  const [memos, setMemos] = useState<Memo[]>([]);
  const [showMemoForm, setShowMemoForm] = useState<boolean>(false);
  const [editingMemo, setEditingMemo] = useState<Memo | null>(null);
  const [memoFormData, setMemoFormData] = useState<string>("");

  const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5173";

  // 企業情報を取得
  const fetchCompany = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/companies/${id}`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data: ApiResponse<CompanyApiResponse> = await response.json();
        if (data.success && data.data?.company) {
          setCompany(data.data.company);
        } else {
          setError("企業情報の取得に失敗しました");
        }
      } else {
        setError("企業情報の取得に失敗しました");
      }
    } catch (error) {
      setError("企業情報の取得中にエラーが発生しました");
    } finally {
      setLoading(false);
    }
  }, [id, API_BASE_URL]);

  // 応募状況を取得
  const fetchApplication = useCallback(async () => {
    if (!id) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/applications`, {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data: ApiResponse<ApplicationWithCompany[]> =
          await response.json();
        if (data.success && Array.isArray(data.data)) {
          const app = data.data.find((a) => a.company_id === parseInt(id, 10));
          setApplication(app || null);
        }
      }
    } catch (error) {
      // エラーハンドリング
    }
  }, [id, API_BASE_URL]);

  // メモを取得
  const fetchMemos = useCallback(async () => {
    if (!id) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/companies/${id}/memos`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        const data: ApiResponse<{ memos: Memo[] }> = await response.json();
        if (data.success && data.data?.memos) {
          setMemos(data.data.memos);
        }
      }
    } catch (error) {
      // エラーハンドリング
    }
  }, [id, API_BASE_URL]);

  useEffect(() => {
    const fetchData = async () => {
      await Promise.all([fetchCompany(), fetchApplication(), fetchMemos()]);
    };
    fetchData();
  }, [fetchCompany, fetchApplication, fetchMemos]);

  // 企業情報を更新
  const handleUpdateCompany = async (data: CreateCompanyRequest) => {
    if (!id) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/companies/${id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setIsEditingCompany(false);
        fetchCompany();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "企業情報の更新に失敗しました");
      }
    } catch (error) {
      setError("企業情報の更新中にエラーが発生しました");
    }
  };

  // 企業を削除
  const handleDeleteCompany = async () => {
    if (!id || !window.confirm("この企業を削除しますか？")) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/companies/${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        navigate("/companies");
      } else {
        const errorData = await response.json();
        setError(errorData.message || "企業の削除に失敗しました");
      }
    } catch (error) {
      setError("企業の削除中にエラーが発生しました");
    }
  };

  // 応募状況を追加
  const handleAddApplication = async (data: CreateApplicationRequest) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/applications`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setIsEditingApplication(false);
        fetchApplication();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "応募状況の登録に失敗しました");
      }
    } catch (error) {
      setError("応募状況の登録中にエラーが発生しました");
    }
  };

  // 応募状況を更新
  const handleUpdateApplication = async (data: UpdateApplicationRequest) => {
    if (!application) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/applications/${application.id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        }
      );

      if (response.ok) {
        setIsEditingApplication(false);
        fetchApplication();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "応募状況の更新に失敗しました");
      }
    } catch (error) {
      setError("応募状況の更新中にエラーが発生しました");
    }
  };

  // 応募状況を削除
  const handleDeleteApplication = async () => {
    if (!application || !window.confirm("この応募状況を削除しますか？")) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/applications/${application.id}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        fetchApplication();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "応募状況の削除に失敗しました");
      }
    } catch (error) {
      setError("応募状況の削除中にエラーが発生しました");
    }
  };

  // メモフォームをリセット
  const resetMemoForm = () => {
    setMemoFormData("");
    setEditingMemo(null);
    setShowMemoForm(false);
  };

  // メモを追加
  const handleAddMemo = async () => {
    if (!id || !memoFormData.trim()) {
      setError("メモ内容を入力してください");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/companies/${id}/memos`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ content: memoFormData }),
        }
      );

      if (response.ok) {
        resetMemoForm();
        fetchMemos();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "メモの登録に失敗しました");
      }
    } catch (error) {
      setError("メモの登録中にエラーが発生しました");
    }
  };

  // メモを更新
  const handleUpdateMemo = async () => {
    if (!id || !editingMemo || !memoFormData.trim()) {
      setError("メモ内容を入力してください");
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/companies/${id}/memos/${editingMemo.id}`,
        {
          method: "PUT",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ content: memoFormData }),
        }
      );

      if (response.ok) {
        resetMemoForm();
        fetchMemos();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "メモの更新に失敗しました");
      }
    } catch (error) {
      setError("メモの更新中にエラーが発生しました");
    }
  };

  // メモを削除
  const handleDeleteMemo = async (memoId: number) => {
    if (!id || !window.confirm("このメモを削除しますか？")) return;

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/companies/${id}/memos/${memoId}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        fetchMemos();
        setError(null);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "メモの削除に失敗しました");
      }
    } catch (error) {
      setError("メモの削除中にエラーが発生しました");
    }
  };

  // メモ編集ボタン
  const handleEditMemo = (memo: Memo) => {
    setEditingMemo(memo);
    setMemoFormData(memo.content);
    setShowMemoForm(true);
  };

  // ステータスラベルを取得
  const getStatusLabel = (status: string): string => {
    const statusMap: { [key: string]: string } = {
      applied: "応募済み",
      document_screening: "書類選考中",
      interview_scheduled: "面接予定",
      final_interview: "最終面接",
      offer: "内定",
      rejected: "不合格",
      declined: "辞退",
    };
    return statusMap[status] || status;
  };

  // ステータスの色を取得
  const getStatusColor = (status: string): string => {
    const colorMap: { [key: string]: string } = {
      applied: "bg-blue-100 text-blue-800",
      document_screening: "bg-yellow-100 text-yellow-800",
      interview_scheduled: "bg-purple-100 text-purple-800",
      final_interview: "bg-orange-100 text-orange-800",
      offer: "bg-green-100 text-green-800",
      rejected: "bg-red-100 text-red-800",
      declined: "bg-gray-100 text-gray-800",
    };
    return colorMap[status] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-16 text-gray-600">
        <div className="loading-spinner"></div>
        <span>読み込み中...</span>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="text-center py-16">
          <h2 className="text-2xl text-gray-600 mb-4">企業が見つかりません</h2>
          <button
            onClick={() => navigate("/companies")}
            className="bg-blue-600 text-white border-none py-2 px-6 rounded-lg cursor-pointer hover:bg-blue-700"
          >
            企業一覧に戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-8">
      <div className="mb-6">
        <button
          onClick={() => navigate("/companies")}
          className="text-blue-600 hover:text-blue-700 flex items-center gap-2"
        >
          ← 企業一覧に戻る
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button
            onClick={() => setError(null)}
            className="bg-transparent border-none text-red-700 text-xl cursor-pointer px-2"
          >
            ×
          </button>
        </div>
      )}

      {/* 企業情報セクション */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-start mb-4">
          <h1 className="text-3xl font-bold text-gray-800">{company.name}</h1>
          <div className="flex gap-2">
            {!isEditingCompany && (
              <>
                <button
                  onClick={() => setIsEditingCompany(true)}
                  className="py-2 px-4 bg-blue-600 text-white border-none rounded-md text-sm cursor-pointer transition-colors hover:bg-blue-700"
                >
                  編集
                </button>
                <button
                  onClick={handleDeleteCompany}
                  className="py-2 px-4 bg-red-600 text-white border-none rounded-md text-sm cursor-pointer transition-colors hover:bg-red-700"
                >
                  削除
                </button>
              </>
            )}
          </div>
        </div>

        {isEditingCompany ? (
          <CompanyForm
            initialData={company}
            onSubmit={handleUpdateCompany}
            onCancel={() => setIsEditingCompany(false)}
            isEditMode={true}
          />
        ) : (
          <div className="space-y-3">
            <div>
              <span className="font-semibold text-gray-700">業界: </span>
              <span className="text-gray-600">{company.industry || "-"}</span>
            </div>
            <div>
              <span className="font-semibold text-gray-700">
                ウェブサイト:{" "}
              </span>
              {company.website ? (
                <a
                  href={company.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {company.website}
                </a>
              ) : (
                <span className="text-gray-600">-</span>
              )}
            </div>
            <div>
              <span className="font-semibold text-gray-700">
                マイページURL:{" "}
              </span>
              {company.mypage_url ? (
                <a
                  href={company.mypage_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  {company.mypage_url}
                </a>
              ) : (
                <span className="text-gray-600">-</span>
              )}
            </div>
            <div>
              <span className="font-semibold text-gray-700">
                マイページID:{" "}
              </span>
              <span className="text-gray-600">{company.mypage_id || "-"}</span>
            </div>
            <div>
              <span className="font-semibold text-gray-700">志望度: </span>
              <span className="text-gray-600">
                {company.interest_level
                  ? "★".repeat(company.interest_level) +
                    "☆".repeat(5 - company.interest_level)
                  : "-"}
              </span>
            </div>
            {(company.salary_min || company.salary_max) && (
              <div>
                <span className="font-semibold text-gray-700">想定年収: </span>
                <span className="text-gray-600">
                  {company.salary_min && company.salary_max
                    ? `${company.salary_min}万円 〜 ${company.salary_max}万円`
                    : company.salary_min
                      ? `${company.salary_min}万円〜`
                      : `〜${company.salary_max}万円`}
                </span>
              </div>
            )}

            {company.salary_ave && (
              <div>
                <span className="font-semibold text-gray-700">平均年収: </span>
                <span className="text-gray-600">{company.salary_ave}万円</span>
              </div>
            )}
            {company.holidays && (
              <div>
                <span className="font-semibold text-gray-700">休日: </span>
                <span className="text-gray-600">{company.holidays}日</span>
              </div>
            )}
            {company.employee_count && (
              <div>
                <span className="font-semibold text-gray-700">従業員数: </span>
                <span className="text-gray-600">
                  {company.employee_count}人
                </span>
              </div>
            )}
            {company.location && (
              <div>
                <span className="font-semibold text-gray-700">勤務地: </span>
                <span className="text-gray-600">{company.location}</span>
              </div>
            )}
            {company.benefits && (
              <div>
                <span className="font-semibold text-gray-700">福利厚生: </span>
                <span className="text-gray-600">{company.benefits}</span>
              </div>
            )}
            <div>
              <span className="font-semibold text-gray-700">登録日: </span>
              <span className="text-gray-600">
                {new Date(company.created_at).toLocaleDateString("ja-JP")}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* 応募状況セクション */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-800">応募状況</h2>
          {!application && !isEditingApplication && (
            <button
              onClick={() => setIsEditingApplication(true)}
              className="py-2 px-4 bg-green-600 text-white border-none rounded-md text-sm cursor-pointer transition-colors hover:bg-green-700"
            >
              応募登録
            </button>
          )}
          {application && !isEditingApplication && (
            <div className="flex gap-2">
              <button
                onClick={() => setIsEditingApplication(true)}
                className="py-2 px-4 bg-blue-600 text-white border-none rounded-md text-sm cursor-pointer transition-colors hover:bg-blue-700"
              >
                編集
              </button>
              <button
                onClick={handleDeleteApplication}
                className="py-2 px-4 bg-red-600 text-white border-none rounded-md text-sm cursor-pointer transition-colors hover:bg-red-700"
              >
                削除
              </button>
            </div>
          )}
        </div>

        {isEditingApplication ? (
          <ApplicationForm
            companyId={company.id}
            initialData={
              application
                ? {
                    company_id: application.company_id,
                    status: application.status,
                  }
                : undefined
            }
            onSubmit={
              application
                ? (data) => handleUpdateApplication({ status: data.status })
                : handleAddApplication
            }
            onCancel={() => setIsEditingApplication(false)}
            isEditing={!!application}
          />
        ) : application ? (
          <div className="space-y-3">
            <div>
              <span className="font-semibold text-gray-700">ステータス: </span>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(
                  application.status
                )}`}
              >
                {getStatusLabel(application.status)}
              </span>
            </div>
            <div>
              <span className="font-semibold text-gray-700">登録日: </span>
              <span className="text-gray-600">
                {new Date(application.created_at).toLocaleDateString("ja-JP")}
              </span>
            </div>
            <div>
              <span className="font-semibold text-gray-700">更新日: </span>
              <span className="text-gray-600">
                {new Date(application.updated_at).toLocaleDateString("ja-JP")}
              </span>
            </div>
          </div>
        ) : (
          <p className="text-gray-500">応募状況が登録されていません</p>
        )}
      </div>

      {/* メモセクション */}
      <div className="bg-white rounded-lg shadow-md p-6 mt-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-800">メモ</h2>
          <button
            onClick={() => setShowMemoForm(true)}
            className="py-2 px-4 bg-indigo-600 text-white border-none rounded-md text-sm cursor-pointer transition-colors hover:bg-indigo-700"
          >
            メモ追加
          </button>
        </div>

        {/* メモフォーム */}
        {showMemoForm && (
          <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold mb-3">
              {editingMemo ? "メモ編集" : "新規メモ"}
            </h3>
            <textarea
              value={memoFormData}
              onChange={(e) => setMemoFormData(e.target.value)}
              placeholder="メモ内容を入力してください"
              rows={4}
              className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 mb-3"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={resetMemoForm}
                className="py-2 px-4 bg-gray-100 text-gray-600 border border-gray-300 rounded-md text-sm cursor-pointer transition-colors hover:bg-gray-200"
              >
                キャンセル
              </button>
              <button
                onClick={editingMemo ? handleUpdateMemo : handleAddMemo}
                className="py-2 px-4 bg-indigo-600 text-white border-none rounded-md text-sm cursor-pointer transition-colors hover:bg-indigo-700"
              >
                {editingMemo ? "更新" : "追加"}
              </button>
            </div>
          </div>
        )}

        {/* メモ一覧 */}
        {memos.length === 0 ? (
          <p className="text-gray-500">メモがまだありません</p>
        ) : (
          <div className="space-y-3">
            {memos.map((memo) => (
              <div
                key={memo.id}
                className="p-4 bg-gray-50 rounded-lg border border-gray-200"
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="text-sm text-gray-500">
                    {new Date(memo.updated_at).toLocaleString("ja-JP")}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEditMemo(memo)}
                      className="px-3 py-1 bg-blue-50 text-blue-600 border-none rounded-md text-xs cursor-pointer transition-colors hover:bg-blue-100"
                    >
                      編集
                    </button>
                    <button
                      onClick={() => handleDeleteMemo(memo.id)}
                      className="px-3 py-1 bg-red-50 text-red-600 border-none rounded-md text-xs cursor-pointer transition-colors hover:bg-red-100"
                    >
                      削除
                    </button>
                  </div>
                </div>
                <div className="text-gray-700 whitespace-pre-wrap">
                  {memo.content}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// 企業情報フォーム
interface CompanyFormProps {
  initialData: Company;
  onSubmit: (data: CreateCompanyRequest) => void;
  onCancel: () => void;
  isEditMode: boolean;
}

const CompanyForm: React.FC<CompanyFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<CreateCompanyRequest>({
    name: initialData.name,
    industry: initialData.industry || "",
    website: initialData.website || "",
    mypage_url: initialData.mypage_url || "",
    mypage_id: initialData.mypage_id || "",
    mypage_password: "",
    interest_level: initialData.interest_level || undefined,
    salary_min: initialData.salary_min || undefined,
    salary_max: initialData.salary_max || undefined,
    salary_ave: initialData.salary_ave || undefined,
    holidays: initialData.holidays || undefined,
    employee_count: initialData.employee_count || undefined,
    benefits: initialData.benefits || "",
    location: initialData.location || "",
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      alert("企業名は必須です");
      return;
    }
    onSubmit(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="name" className="block mb-2 font-medium text-gray-800">
          企業名 *
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        />
      </div>

      <div>
        <label
          htmlFor="industry"
          className="block mb-2 font-medium text-gray-800"
        >
          業界
        </label>
        <input
          type="text"
          id="industry"
          name="industry"
          value={formData.industry}
          onChange={handleChange}
          className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        />
      </div>

      <div>
        <label
          htmlFor="website"
          className="block mb-2 font-medium text-gray-800"
        >
          ウェブサイト
        </label>
        <input
          type="url"
          id="website"
          name="website"
          value={formData.website}
          onChange={handleChange}
          className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        />
      </div>

      <div>
        <label
          htmlFor="mypage_url"
          className="block mb-2 font-medium text-gray-800"
        >
          採用マイページURL
        </label>
        <input
          type="url"
          id="mypage_url"
          name="mypage_url"
          value={formData.mypage_url}
          onChange={handleChange}
          className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        />
      </div>

      <div>
        <label
          htmlFor="mypage_id"
          className="block mb-2 font-medium text-gray-800"
        >
          マイページログインID
        </label>
        <input
          type="text"
          id="mypage_id"
          name="mypage_id"
          value={formData.mypage_id}
          onChange={handleChange}
          className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        />
      </div>

      <div>
        <label
          htmlFor="interest_level"
          className="block mb-2 font-medium text-gray-800"
        >
          志望度
        </label>
        <select
          id="interest_level"
          name="interest_level"
          value={formData.interest_level || ""}
          onChange={(e) =>
            setFormData({
              ...formData,
              interest_level: e.target.value
                ? parseInt(e.target.value)
                : undefined,
            })
          }
          className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        >
          <option value="">選択してください</option>
          <option value="1">★☆☆☆☆ (1)</option>
          <option value="2">★★☆☆☆ (2)</option>
          <option value="3">★★★☆☆ (3)</option>
          <option value="4">★★★★☆ (4)</option>
          <option value="5">★★★★★ (5)</option>
        </select>
      </div>

      <div>
        <label
          htmlFor="salary_min"
          className="block mb-2 font-medium text-gray-800"
        >
          想定年収（最小）
        </label>
        <input
          type="number"
          id="salary_min"
          name="salary_min"
          value={formData.salary_min || ""}
          onChange={(e) =>
            setFormData({
              ...formData,
              salary_min: e.target.value ? parseInt(e.target.value) : undefined,
            })
          }
          placeholder="400"
          className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        />
        <p className="text-sm text-gray-500 mt-1">万円単位で入力してください</p>
      </div>
      <div>
        <label
          htmlFor="salary_max"
          className="block mb-2 font-medium text-gray-800"
        >
          想定年収（最大）
        </label>
        <input
          type="number"
          id="salary_max"
          name="salary_max"
          value={formData.salary_max || ""}
          onChange={(e) =>
            setFormData({
              ...formData,
              salary_max: e.target.value ? parseInt(e.target.value) : undefined,
            })
          }
          placeholder="600"
          className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        />
        <p className="text-sm text-gray-500 mt-1">万円単位で入力してください</p>
      </div>

      <div>
        <label
          htmlFor="salary_average"
          className="block mb-2 font-medium text-gray-800"
        >
          平均年収
        </label>
        <input
          type="number"
          id="salary_ave"
          name="salary_ave"
          value={formData.salary_ave || ""}
          onChange={(e) =>
            setFormData({
              ...formData,
              salary_ave: e.target.value ? parseInt(e.target.value) : undefined,
            })
          }
          placeholder="600"
          className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        />
        <p className="text-sm text-gray-500 mt-1">万円単位で入力してください</p>
      </div>

      <div>
        <label
          htmlFor="holidays"
          className="block mb-2 font-medium text-gray-800"
        >
          年間休日
        </label>
        <input
          type="number"
          id="holidays"
          name="holidays"
          value={formData.holidays || ""}
          onChange={(e) =>
            setFormData({
              ...formData,
              holidays: e.target.value ? parseInt(e.target.value) : undefined,
            })
          }
          placeholder="120"
          className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        />
        <p className="text-sm text-gray-500 mt-1">日数を入力してください</p>
      </div>

      <div>
        <label
          htmlFor="employee_count"
          className="block mb-2 font-medium text-gray-800"
        >
          従業員数
        </label>
        <input
          type="number"
          id="employee_count"
          name="employee_count"
          value={formData.employee_count || ""}
          onChange={(e) =>
            setFormData({
              ...formData,
              employee_count: e.target.value
                ? parseInt(e.target.value)
                : undefined,
            })
          }
          placeholder="1000"
          className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        />
        <p className="text-sm text-gray-500 mt-1">従業員数を入力してください</p>
      </div>

      <div>
        <label
          htmlFor="location"
          className="block mb-2 font-medium text-gray-800"
        >
          勤務地
        </label>
        <input
          type="text"
          id="location"
          name="location"
          value={formData.location}
          onChange={handleChange}
          placeholder="東京都渋谷区"
          className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        />
      </div>

      <div>
        <label
          htmlFor="benefits"
          className="block mb-2 font-medium text-gray-800"
        >
          福利厚生
        </label>
        <textarea
          id="benefits"
          name="benefits"
          value={formData.benefits}
          onChange={(e) =>
            setFormData({
              ...formData,
              benefits: e.target.value,
            })
          }
          placeholder="社会保険完備、リモートワーク可、書籍購入補助"
          rows={4}
          className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        />
      </div>

      <div className="flex gap-4 justify-end pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="bg-gray-100 text-gray-600 border border-gray-300 py-2 px-6 rounded-lg text-base cursor-pointer transition-all hover:bg-gray-200"
        >
          キャンセル
        </button>
        <button
          type="submit"
          className="bg-gradient-to-br from-blue-500 to-green-600 text-white border-none py-2 px-6 rounded-lg text-base font-medium cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-lg"
        >
          更新
        </button>
      </div>
    </form>
  );
};

// 応募状況フォーム
interface ApplicationFormProps {
  companyId: number;
  initialData?: CreateApplicationRequest;
  onSubmit: (data: CreateApplicationRequest) => void;
  onCancel: () => void;
  isEditing: boolean;
}

const ApplicationForm: React.FC<ApplicationFormProps> = ({
  companyId,
  initialData,
  onSubmit,
  onCancel,
  isEditing,
}) => {
  const [formData, setFormData] = useState<CreateApplicationRequest>(
    initialData || {
      company_id: companyId,
      status: "",
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.status) {
      alert("ステータスを選択してください");
      return;
    }
    onSubmit(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      status: value,
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label
          htmlFor="status"
          className="block mb-2 font-medium text-gray-800"
        >
          ステータス *
        </label>
        <select
          id="status"
          name="status"
          value={formData.status}
          onChange={handleChange}
          required
          className="w-full p-3 border border-gray-300 rounded-lg text-base transition-all focus:outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
        >
          <option value="">ステータスを選択してください</option>
          <option value="applied">応募済み</option>
          <option value="document_screening">書類選考中</option>
          <option value="interview_scheduled">面接予定</option>
          <option value="final_interview">最終面接</option>
          <option value="offer">内定</option>
          <option value="rejected">不合格</option>
          <option value="declined">辞退</option>
        </select>
      </div>

      <div className="flex gap-4 justify-end pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="bg-gray-100 text-gray-600 border border-gray-300 py-2 px-6 rounded-lg text-base cursor-pointer transition-all hover:bg-gray-200"
        >
          キャンセル
        </button>
        <button
          type="submit"
          className="bg-gradient-to-br from-blue-500 to-green-600 text-white border-none py-2 px-6 rounded-lg text-base font-medium cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-lg"
        >
          {isEditing ? "更新" : "登録"}
        </button>
      </div>
    </form>
  );
};

export default CompanyDetailPage;
